// ** Modules
import React from 'react';
import axios from 'axios';

// ** UI
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme, type Theme } from '@mui/material/styles';

// ** Utils
import { getStrapiImageUrl } from '@/utils/api/strapi';
import { buildPathFromRelationLink } from '@/utils/ui/navigationUtils';

// ** Components
import Section from '@/components/common/Section';
import { NavGrid, NavCard, type NavCardModel, type NavGridColumns } from '@/components/common/NavGrid';

// ** Types
import type { StrapiComponentProps } from '@/types/strapi';
import type { AuthentikGroupOption, AuthentikGroupResponse } from '@/types/authentik';
import type { ComponentBlocksGridNavigation as BlocksGridNavigationProps } from '@/types/graphql';

type ComponentBlocksGridNavigationProps = StrapiComponentProps<BlocksGridNavigationProps> & {
	navGrigColumnCount?: NavGridColumns;
};

const ComponentBlocksGridNavigation = ({
	host,
	gridItems,
	currentUser,
	containerSx,
	navGrigColumnCount
}: ComponentBlocksGridNavigationProps) => {
	const theme = useTheme();
	const themeMode = theme.palette.mode;
	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	const [_authentikLoading, setAuthentikLoading] = React.useState(true);
	const [authentikGroups, setAuthentikGroups] = React.useState<Map<string, string>>(new Map());

	// 1. Fetch Authentik groups
	const fetchAuthentikGroups = React.useCallback(async () => {
		try {
			const response: AuthentikGroupResponse = await axios.get('/api/sso-groups');
			const groups: AuthentikGroupOption[] = Array.isArray(response.data) ? response.data : [];

			const groupMap = new Map<string, string>();

			groups
				.filter((group) =>
					['Ausschuss_', 'Funktion_', 'Position_', 'Tarif_', 'MSM_', '02 Ehrenamt', '02 Fachausschuss NRW'].some(
						(prefix) => group.name?.startsWith(prefix)
					)
				)
				.forEach((group) => {
					if (group.name && group.pk) {
						groupMap.set(group.name, group.pk);
					}
				});

			setAuthentikGroups(groupMap);
		} catch (error) {
			console.warn('⚠️ Authentik groups fetch failed:', error);
			setAuthentikGroups(new Map());
		} finally {
			setAuthentikLoading(false);
		}
	}, []);

	React.useEffect(() => {
		fetchAuthentikGroups();
	}, [fetchAuthentikGroups]);

	// 2. Permission Filtering
	const filteredGridItems = React.useMemo(() => {
		const userGroups: string[] = currentUser?.groups || [];
		const userGroupIds = userGroups.map((name) => authentikGroups.get(name)).filter((id): id is string => Boolean(id));

		return (gridItems || []).filter((item) => {
			if (!item) return false;

			let restrictedGroupIds: string[] = [];
			try {
				const rawGroups = item.restrictedToGroups ? String(item.restrictedToGroups) : '';
				restrictedGroupIds = rawGroups ? JSON.parse(rawGroups) : [];
			} catch {
				restrictedGroupIds = [];
			}

			return restrictedGroupIds.length === 0 || restrictedGroupIds.some((id) => userGroupIds.includes(id));
		});
	}, [gridItems, currentUser?.groups, authentikGroups]);

	// 3. Mapping to NavCardModel
	const mappedCards: NavCardModel[] = filteredGridItems.map((item, index) => {
		const itemImageData = {
			backgroundDarkImage: item?.gridNavigationItemImage || item?.url?.pageHero?.heroImageDarkMode,
			backgroundLightImage: item?.gridNavigationItemImage || item?.url?.pageHero?.heroImage
		};

		const strapiImageUrl = getStrapiImageUrl({
			host,
			imageData: itemImageData,
			isMobile,
			themeMode
		});

		return {
			id: item?.id || `${index}`,
			headline: item?.title || 'Unbenanntes Element',
			copy: item?.gridNavigationItemDescription || item?.url?.pageDescription || '',
			ctaLabel: item?.gridNavigationItemCtaLabel || 'Mehr erfahren',
			href: item?.gridNavigationItemExternalUrl
				? item.gridNavigationItemExternalUrl
				: item?.url?.slug
					? buildPathFromRelationLink(item.url)
					: '#',
			imageUrl: strapiImageUrl || '/images/drafts/nav-grid/nav-grid-1.png'
		};
	});

	if (mappedCards.length === 0) return null;

	return (
		<Section containerSx={containerSx}>
			<Box sx={{ backgroundColor: 'common.white' }}>
				<NavGrid columns={navGrigColumnCount}>
					{mappedCards.map((card) => (
						<NavCard key={card.id} columns={navGrigColumnCount} {...card} />
					))}
				</NavGrid>
			</Box>
		</Section>
	);
};

export default ComponentBlocksGridNavigation;
